import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { type Envelope } from './_shared';

// ============================================
// Central CDN analytics  (/cdn/analytics*)
// --------------------------------------------------------------------------
// These are the NORMALISED cross-vendor analytics endpoints — they are NOT the
// per-domain vendor fan-out (`/cdn/domains/:id/...`), so responses are FLAT
// (`res.data.data` is the result object directly, no provider-keyed map).
//   GET /cdn/analytics                     central time series (+ breakdowns)
//   GET /cdn/analytics/usage               per-domain volume + peak bandwidth
//   GET /cdn/analytics/usage/by-project    same, grouped by owning project
//   GET /cdn/analytics/projects            projects that own ≥1 CDN domain
// Ported from the legacy `connectors/central-analytics.ts`.
// ============================================

/** Canonical metric names the central store understands. */
export const CentralMetric = {
  Requests: 'requests',
  Bandwidth: 'bandwidth', // bytes/bucket (SUM)
  BandwidthRate: 'bandwidth_rate', // bps/bucket (peak = MAX over window)
  RPS: 'rps', // requests/s per bucket (peak = MAX)
  Errors4xx: 'errors_4xx',
  Errors5xx: 'errors_5xx',
  CacheHits: 'cache_hits',
  CacheMisses: 'cache_misses',
  ReqHitRatio: 'request_hit_ratio', // 0..1 (AVG)
  ByteHitRatio: 'byte_hit_ratio', // 0..1 (AVG)
  TTFB: 'ttfb', // ms (AVG)
  Status: 'status', // breakdown=code → by_dimension keyed 2xx/3xx/4xx/5xx/<code>
  GeoRequests: 'geo_requests', // breakdown=country → by_dimension keyed by country name
  GeoVolume: 'geo_volume',
} as const;
export type CentralMetricName = (typeof CentralMetric)[keyof typeof CentralMetric];

export type AnalyticsPeriod = '1h' | '6h' | '24h' | 'today' | 'last_day' | '7d' | '30d' | 'this_month' | 'last_month';
export type Granularity = '5m' | '1h' | 'day';

/**
 * Granularities a period may be viewed at. Short windows (≤24h) can drop to the
 * 5m tier — the store keeps 5m for ~48h, so last 24h / today / yesterday all
 * have it. Longer windows only have the day tier. The FIRST entry is the natural
 * default for the period; extra entries are user-selectable overrides.
 */
export function allowedGranularities(period: AnalyticsPeriod): Granularity[] {
  switch (period) {
    case '1h':
      return ['5m'];
    case '6h':
    case '24h':
    case 'today':
    case 'last_day':
      return ['1h', '5m'];
    default:
      return ['day'];
  }
}

export interface CentralSeriesPoint {
  timestamp: string; // RFC3339
  value: number;
}
export interface CentralQueryResult {
  metric: string;
  granularity: Granularity;
  aggregation: 'sum' | 'avg';
  series: CentralSeriesPoint[];
  by_provider?: Record<string, CentralSeriesPoint[]>;
  by_dimension?: Record<string, CentralSeriesPoint[]>;
}

/**
 * Map a UI period to an absolute `{ from, to }` window + the granularity tier
 * the store keeps at that depth (5m hot buffer ≈3h, 1h warm ≈48h, day cold).
 * Mirrors the legacy `periodToRange`.
 */
export function periodToRange(
  period: AnalyticsPeriod,
  granularityOverride?: Granularity,
): { from: string; to: string; granularity: Granularity } {
  const now = new Date();
  const iso = (d: Date) => d.toISOString();
  const ago = (ms: number) => iso(new Date(now.getTime() - ms));
  // Local midnight — "today"/"last_day" follow the viewer's calendar day, not UTC.
  const startOfLocalDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const H = 3600_000;
  const D = 24 * H;
  const base = ((): { from: string; to: string; granularity: Granularity } => {
    switch (period) {
      case '1h':
        return { from: ago(H), to: iso(now), granularity: '5m' };
      case '6h':
        return { from: ago(6 * H), to: iso(now), granularity: '1h' };
      case '24h':
        return { from: ago(24 * H), to: iso(now), granularity: '1h' };
      case 'today': {
        const start = startOfLocalDay(now);
        return { from: iso(start), to: iso(now), granularity: '1h' };
      }
      case 'last_day': {
        const startToday = startOfLocalDay(now);
        const start = new Date(startToday.getTime() - D);
        return { from: iso(start), to: iso(startToday), granularity: '1h' };
      }
      case '7d':
        return { from: ago(7 * D), to: iso(now), granularity: 'day' };
      case '30d':
        return { from: ago(30 * D), to: iso(now), granularity: 'day' };
      case 'this_month': {
        const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
        return { from: iso(start), to: iso(now), granularity: 'day' };
      }
      case 'last_month': {
        const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
        const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
        return { from: iso(start), to: iso(end), granularity: 'day' };
      }
    }
  })();
  // Apply an explicit granularity only when it's valid for this window (e.g. 5m
  // for last 24h); otherwise keep the period's natural tier.
  if (granularityOverride && allowedGranularities(period).includes(granularityOverride)) {
    return { ...base, granularity: granularityOverride };
  }
  return base;
}

/**
 * Pick a sensible granularity tier for an arbitrary custom window. Mirrors the
 * preset tiers: short windows render hourly, longer windows roll up to day. We
 * avoid the 5m tier here — its retention is too short to guarantee coverage of a
 * user-chosen historical range.
 */
export function granularityForRange(fromISO: string, toISO: string): Granularity {
  const span = Date.parse(toISO) - Date.parse(fromISO);
  return span <= 2 * 24 * 3600_000 ? '1h' : 'day';
}

/**
 * A time window driving the analytics queries: either one of the named presets
 * (mapped via `periodToRange`) or an absolute custom range the user picked.
 */
export type TimeRange =
  | { kind: 'preset'; period: AnalyticsPeriod }
  | { kind: 'custom'; from: string; to: string };

/** Resolve a `TimeRange` to the absolute `{ from, to, granularity }` queries need. */
export function resolveTimeRange(r: TimeRange): { from: string; to: string; granularity: Granularity } {
  return r.kind === 'custom'
    ? { from: r.from, to: r.to, granularity: granularityForRange(r.from, r.to) }
    : periodToRange(r.period);
}

/** Poll cadence per tier — don't over-fetch cold history. */
function refetchForGranularity(g: Granularity): number {
  return g === '5m' ? 60_000 : g === '1h' ? 300_000 : 1_800_000;
}

export interface CentralSeriesArgs {
  /** 'all' (org-wide) | '<uuid>' | '<uuid>,<uuid>'. */
  domainId?: string;
  /** undefined = no project filter; else comma-joined project UUIDs. */
  projectIds?: string;
  period: AnalyticsPeriod;
  /** Optional finer tier (e.g. '5m') for short windows; ignored if invalid. */
  granularity?: Granularity;
  /**
   * Explicit absolute window — overrides `period`/`periodToRange` when set (used
   * by the custom date-range picker). Must be a referentially stable object
   * (store it in state) or the query key churns every render.
   */
  range?: { from: string; to: string; granularity: Granularity };
  metric: CentralMetricName;
  groupBy?: 'provider';
  breakdown?: 'code' | 'country';
  enabled?: boolean;
}

export function useCentralSeries(args: CentralSeriesArgs) {
  const { domainId = 'all', projectIds, period, granularity: granOverride, range, metric, groupBy, breakdown, enabled = true } = args;
  const { from, to, granularity } = range ?? periodToRange(period, granOverride);
  // Preset windows key on `period` (stable) to avoid the `now`-churn refetch
  // loop; custom windows key on the user-fixed from/to instead so distinct
  // ranges don't collide.
  const windowKey = range ? `custom:${from}:${to}` : period;
  return useQuery({
    queryKey: [
      'cdn',
      'central-analytics',
      domainId,
      projectIds ?? '',
      metric,
      windowKey,
      granularity,
      groupBy ?? 'agg',
      breakdown ?? '',
    ],
    enabled,
    retry: false,
    refetchInterval: refetchForGranularity(granularity),
    queryFn: async (): Promise<CentralQueryResult | null> => {
      const params = new URLSearchParams({ metric, from, to, granularity });
      if (domainId && domainId !== 'all') params.set('domain_ids', domainId);
      if (projectIds) params.set('project_ids', projectIds);
      if (groupBy) params.set('group_by', groupBy);
      if (breakdown) params.set('breakdown', breakdown);
      try {
        const res = await serviceApiV3.get<Envelope<CentralQueryResult>>(`/cdn/analytics?${params.toString()}`);
        return res.data?.data ?? null;
      } catch {
        return null;
      }
    },
  });
}

// ---- Usage ---------------------------------------------------------------
export interface DomainUsageRow {
  domain_id: string;
  domain_name: string;
  volume_bytes: number;
  peak_bandwidth_bps: number;
}
export interface DomainUsageResult {
  granularity: string;
  from: string;
  to: string;
  domains: DomainUsageRow[];
}
export interface ProjectUsageGroup {
  project_id: string; // '' = unassigned
  project_name: string;
  volume_bytes: number;
  domain_count: number;
  domains: DomainUsageRow[];
}
export interface UsageByProjectResult {
  granularity: string;
  from: string;
  to: string;
  projects: ProjectUsageGroup[];
}

export function useDomainUsage(range: { from: string; to: string } | null) {
  const granularity = range ? granularityForRange(range.from, range.to) : 'day';
  return useQuery({
    queryKey: ['cdn', 'analytics', 'usage', range?.from ?? '', range?.to ?? '', granularity],
    enabled: !!range,
    retry: false,
    queryFn: async (): Promise<DomainUsageRow[]> => {
      const p = new URLSearchParams({ from: range!.from, to: range!.to, granularity });
      try {
        const res = await serviceApiV3.get<Envelope<DomainUsageResult>>(`/cdn/analytics/usage?${p.toString()}`);
        return res.data?.data?.domains ?? [];
      } catch {
        return [];
      }
    },
  });
}

export function useUsageByProject(range: { from: string; to: string } | null) {
  return useQuery({
    queryKey: ['cdn', 'analytics', 'usage-by-project', range?.from ?? '', range?.to ?? ''],
    enabled: !!range,
    retry: false,
    queryFn: async (): Promise<ProjectUsageGroup[]> => {
      const p = new URLSearchParams({ from: range!.from, to: range!.to, granularity: 'day' });
      try {
        const res = await serviceApiV3.get<Envelope<UsageByProjectResult>>(
          `/cdn/analytics/usage/by-project?${p.toString()}`,
        );
        return res.data?.data?.projects ?? [];
      } catch {
        return [];
      }
    },
  });
}

export interface ServiceUsageGroup {
  service_id: string; // canonical (family) service id; '' = unassigned
  service_name: string;
  volume_bytes: number;
  domain_count: number;
  domains: DomainUsageRow[];
}
export interface UsageByServiceResult {
  from: string;
  to: string;
  services: ServiceUsageGroup[];
}

/** Org usage grouped by CDN service family — the top level of Org → Services → Projects. */
export function useUsageByService(range: { from: string; to: string } | null) {
  return useQuery({
    queryKey: ['cdn', 'analytics', 'usage-by-service', range?.from ?? '', range?.to ?? ''],
    enabled: !!range,
    retry: false,
    queryFn: async (): Promise<ServiceUsageGroup[]> => {
      const p = new URLSearchParams({ from: range!.from, to: range!.to });
      try {
        const res = await serviceApiV3.get<Envelope<UsageByServiceResult>>(
          `/cdn/analytics/usage/by-service?${p.toString()}`,
        );
        return res.data?.data?.services ?? [];
      } catch {
        return [];
      }
    },
  });
}

// ---- Headline usage summary (KPI strip) ------------------------------------
export interface UsageSummaryResult {
  /** SUM(bandwidth) — total egress bytes over the window. */
  volume_bytes: number;
  /** MAX over buckets of the simultaneous cross-vendor bandwidth (bps). */
  peak_bandwidth_bps: number;
  /** MAX over buckets of the simultaneous cross-vendor request rate (req/s). */
  peak_rps: number;
  /** Distinct domains that carried any traffic in the window. */
  domain_count: number;
  from: string;
  to: string;
}

/** Org-wide headline KPIs (total volume, peak bandwidth, peak RPS, domain count)
 *  for the Business Usage strip — same best-tier basis as the usage table. */
export function useUsageSummary(range: { from: string; to: string } | null) {
  return useQuery({
    queryKey: ['cdn', 'analytics', 'usage-summary', range?.from ?? '', range?.to ?? ''],
    enabled: !!range,
    retry: false,
    queryFn: async (): Promise<UsageSummaryResult | null> => {
      const p = new URLSearchParams({ from: range!.from, to: range!.to });
      try {
        const res = await serviceApiV3.get<Envelope<UsageSummaryResult>>(
          `/cdn/analytics/usage/summary?${p.toString()}`,
        );
        return res.data?.data ?? null;
      } catch {
        return null;
      }
    },
  });
}

// ---- Billing-basis regions (reconcile split) -------------------------------
export interface UsageRegionsResult {
  /** Raw bytes per billing region. VN already includes the untagged remainder. */
  regions: { VN?: number; CN?: number; TW?: number; TH?: number; OTHER?: number };
  /** Delivered bandwidth the vendors left country-less (already inside regions.VN). */
  untagged_bytes: number;
  from: string;
  to: string;
}

/**
 * Org region split on the EXACT basis statement billing reconciles
 * (VN incl. untagged / CN / TW / TH / OTHER). Summing the buckets reproduces
 * the delivered volume total.
 */
export function useUsageRegions(range: { from: string; to: string } | null) {
  return useQuery({
    queryKey: ['cdn', 'analytics', 'usage-regions', range?.from ?? '', range?.to ?? ''],
    enabled: !!range,
    retry: false,
    queryFn: async (): Promise<UsageRegionsResult | null> => {
      const p = new URLSearchParams({ from: range!.from, to: range!.to });
      try {
        const res = await serviceApiV3.get<Envelope<UsageRegionsResult>>(
          `/cdn/analytics/usage/regions?${p.toString()}`,
        );
        return res.data?.data ?? null;
      } catch {
        return null;
      }
    },
  });
}

// ---- Projects ------------------------------------------------------------
export interface CdnProjectOption {
  project_id: string;
  project_name: string;
  domain_count: number;
}

export function useCdnProjects() {
  return useQuery({
    queryKey: ['cdn', 'analytics', 'projects'],
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<CdnProjectOption[]> => {
      try {
        const res = await serviceApiV3.get<Envelope<{ projects: CdnProjectOption[] }>>(`/cdn/analytics/projects`);
        return res.data?.data?.projects ?? [];
      } catch {
        return [];
      }
    },
  });
}
